import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Film,
  Gamepad2,
  Keyboard,
  Loader2,
  Map,
  Maximize2,
  RotateCcw
} from 'lucide-react';
import GameEmbed from './GameEmbed';
import { NEWTON_SEQUENCE, isNewtonSequenceGame } from './newtonSequence';
import { useStoryTelemetry } from '../services/StoryTelemetry';
import './SparcStoryPlayer.css';

const INTRO_SRC = '/sparc/';
const NAVIGATION_SRC = '/sparc/navigation/';
const TRANSITION_VIDEO_SRC = '/story-assets/sparc/Movie_002.mp4';

const PRELOAD_ITEMS = [
  { id: 'transition', label: 'Transition Video', resources: [TRANSITION_VIDEO_SRC] },
  { id: 'navigation', label: 'Navigation Hub', resources: [NAVIGATION_SRC] },
  { id: 'sickle', label: 'Sickle Cell', resources: ['/sparc/navigation/modules/sickle-cell-game/index.html'] },
  { id: 'oxygen', label: 'Oxygen Exchange', resources: ['/sparc/navigation/modules/oxygen.html', '/sparc/navigation/modules/oxygen-part2.html'] },
  { id: 'wound', label: 'Wound Fixing', resources: ['/sparc/navigation/modules/wound.html'] },
  {
    id: 'heart',
    label: 'Heart Beating',
    resources: [
      '/games/gameheart/Build/heart.loader.js',
      '/games/gameheart/Build/heart.data',
      '/games/gameheart/Build/heart.framework.js',
      '/games/gameheart/Build/heart.wasm'
    ]
  },
  {
    id: 'meeting',
    label: 'Meeting Cells',
    resources: [
      '/games/gamemeetingcells/Build/MeetingCells_NewEnv.loader.js',
      '/games/gamemeetingcells/Build/MeetingCells_NewEnv.data',
      '/games/gamemeetingcells/Build/MeetingCells_NewEnv.framework.js',
      '/games/gamemeetingcells/Build/MeetingCells_NewEnv.wasm'
    ]
  },
  {
    id: 'motion',
    label: 'Red Blood Cell Motion',
    resources: [
      '/games/newton1/Build/newton1.loader.js',
      '/games/newton1/Build/newton1.data',
      '/games/newton1/Build/newton1.framework.js',
      '/games/newton1/Build/newton1.wasm',
      '/games/newton2/Build/newton2.loader.js',
      '/games/newton2/Build/newton2.data',
      '/games/newton2/Build/newton2.framework.js',
      '/games/newton2/Build/newton2.wasm',
      '/games/newton3/Build/newton3.loader.js',
      '/games/newton3/Build/newton3.data',
      '/games/newton3/Build/newton3.framework.js',
      '/games/newton3/Build/newton3.wasm',
      '/games/Force&Motion/Build/20251122DrivingBuild.loader.js',
      '/games/Force&Motion/Build/20251122DrivingBuild.data',
      '/games/Force&Motion/Build/20251122DrivingBuild.framework.js',
      '/games/Force&Motion/Build/20251122DrivingBuild.wasm'
    ]
  }
];

const stageLabels = {
  intro: 'Intro Scene',
  transition: 'Transition Video',
  navigation: 'Navigation Hub',
  'html-module': 'Module Scene',
  'unity-module': 'Unity Module'
};

const helpText = {
  intro: 'Click START, choose a pilot, then SELECT. Use the in-scene objective prompts to finish the first scene.',
  transition: 'The transition video plays automatically. Use the video controls if you need to pause or review.',
  navigation: 'Choose a story module from the navigation scene. Loaded modules stay in this player.',
  'html-module': 'Complete the activity in the frame. Use Return to Hub when you are ready to choose another module.',
  'unity-module': 'Use the module controls inside the game. Press Fullscreen for an uncluttered play view.'
};

const preloadStatusText = {
  queued: 'Queued',
  loading: 'Preparing',
  ready: 'Ready',
  error: 'On demand'
};

const initialPreloadStates = () => (
  PRELOAD_ITEMS.reduce((states, item) => {
    states[item.id] = 'queued';
    return states;
  }, {})
);

const isCompletionEvent = (event = {}) => {
  if (event.completed === true) return true;
  const eventName = String(event.event_name || event.eventType || event.type || '').toLowerCase();
  const status = String(event.status || event.phase || '').toLowerCase();
  const verb = String(event.verb || '').toLowerCase();
  return (
    eventName.includes('complete') ||
    eventName.includes('finish') ||
    status === 'completed' ||
    status === 'complete' ||
    verb.includes('completed')
  );
};

const drainResponse = async (response) => {
  if (response.body && typeof response.body.getReader === 'function') {
    const reader = response.body.getReader();
    while (true) {
      const { done } = await reader.read();
      if (done) return;
    }
  }
  if (typeof response.blob === 'function') {
    await response.blob();
  }
};

const preloadResource = async (url) => {
  if (typeof window.fetch !== 'function') return;
  const response = await window.fetch(url, { cache: 'force-cache' });
  if (!response.ok) {
    throw new Error(`Failed to preload ${url}`);
  }
  await drainResponse(response);
};

const mapModuleToPreloadId = (moduleInfo = {}) => {
  if (moduleInfo.sceneId) return moduleInfo.sceneId;
  if (moduleInfo.gameId === 'gameheart') return 'heart';
  if (moduleInfo.gameId === 'gamemeetingcells') return 'meeting';
  if (isNewtonSequenceGame(moduleInfo.gameId)) return 'motion';
  return 'module';
};

const getModuleTelemetryId = (moduleInfo = {}) => (
  moduleInfo.gameId || moduleInfo.sceneId || moduleInfo.title || 'module'
);

const SparcStoryPlayer = () => {
  const [stage, setStage] = useState('intro');
  const [activeModule, setActiveModule] = useState(null);
  const [preloadStates, setPreloadStates] = useState(initialPreloadStates);
  const theaterRef = useRef(null);
  const { logStoryEvent } = useStoryTelemetry({
    storyId: 'sparc',
    moduleId: 'story-sparc'
  });

  const openNavigation = useCallback(() => {
    logStoryEvent('story_stage_enter', {
      stage: 'navigation',
      sceneId: 'navigation',
      status: 'entered'
    });
    setActiveModule(null);
    setStage('navigation');
  }, [logStoryEvent]);

  const restartStory = useCallback(() => {
    logStoryEvent('story_stage_enter', {
      stage: 'intro',
      sceneId: 'intro',
      status: 'restarted'
    });
    setActiveModule(null);
    setStage('intro');
  }, [logStoryEvent]);

  const handleFullscreen = useCallback(() => {
    if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
      document.exitFullscreen();
      return;
    }
    if (theaterRef.current?.requestFullscreen) {
      theaterRef.current.requestFullscreen();
    }
  }, []);

  const markPreloadReady = useCallback((itemId) => {
    setPreloadStates((current) => (
      current[itemId] === 'ready'
        ? current
        : { ...current, [itemId]: 'ready' }
    ));
  }, []);

  const advanceNewtonSequence = useCallback((event = {}) => {
    if (!isCompletionEvent(event)) return;
    setActiveModule((current) => {
      if (current?.kind !== 'newton-sequence') return current;
      logStoryEvent('story_module_complete', {
        stage: 'unity-module',
        sceneId: current.sceneId || 'motion',
        targetModuleId: current.gameId,
        sequenceId: 'newton-physics-challenge',
        sequenceIndex: current.sequenceIndex,
        completed: true,
        status: 'completed'
      });
      const nextIndex = current.sequenceIndex + 1;
      if (nextIndex >= NEWTON_SEQUENCE.length) {
        logStoryEvent('story_stage_enter', {
          stage: 'navigation',
          sceneId: 'navigation',
          status: 'returned'
        });
        setStage('navigation');
        return null;
      }
      const next = NEWTON_SEQUENCE[nextIndex];
      logStoryEvent('story_stage_enter', {
        stage: 'unity-module',
        sceneId: 'motion',
        targetModuleId: next.gameId,
        sequenceId: 'newton-physics-challenge',
        sequenceIndex: nextIndex,
        status: 'entered'
      });
      return {
        ...current,
        title: next.title,
        gameId: next.gameId,
        sequenceIndex: nextIndex
      };
    });
  }, [logStoryEvent]);

  const handleStoryNavigate = useCallback((payload = {}) => {
    const inferredKind = payload.kind || (payload.gameId ? 'unity-module' : 'html-module');
    const moduleTitle = payload.title || payload.name || payload.sceneId || 'SPARC module';

    if (inferredKind === 'unity-module' && payload.gameId) {
      if (isNewtonSequenceGame(payload.gameId) || payload.sceneId === 'motion') {
        logStoryEvent('story_module_open', {
          stage: 'unity-module',
          sceneId: 'motion',
          targetModuleId: NEWTON_SEQUENCE[0].gameId,
          sequenceId: 'newton-physics-challenge',
          sequenceIndex: 0,
          status: 'opened'
        });
        setActiveModule({
          kind: 'newton-sequence',
          title: NEWTON_SEQUENCE[0].title,
          sceneId: 'motion',
          gameId: NEWTON_SEQUENCE[0].gameId,
          sequenceIndex: 0
        });
        setStage('unity-module');
        return;
      }
      logStoryEvent('story_module_open', {
        stage: 'unity-module',
        sceneId: payload.sceneId || payload.from || payload.gameId,
        targetModuleId: payload.gameId,
        status: 'opened'
      });
      setActiveModule({
        kind: 'unity-module',
        title: moduleTitle,
        sceneId: payload.sceneId || payload.from || payload.gameId,
        gameId: payload.gameId
      });
      setStage('unity-module');
      return;
    }

    if (payload.url) {
      logStoryEvent('story_module_open', {
        stage: 'html-module',
        sceneId: payload.sceneId || 'html-module',
        targetModuleId: payload.sceneId || moduleTitle,
        status: 'opened'
      });
      setActiveModule({
        kind: 'html-module',
        title: moduleTitle,
        sceneId: payload.sceneId || 'html-module',
        src: payload.url
      });
      setStage('html-module');
    }
  }, [logStoryEvent]);

  useEffect(() => {
    logStoryEvent('story_stage_enter', {
      stage: 'intro',
      sceneId: 'intro',
      status: 'entered'
    });
  }, [logStoryEvent]);

  useEffect(() => {
    let cancelled = false;

    const runPreloadQueue = async () => {
      for (const item of PRELOAD_ITEMS) {
        if (cancelled) return;
        setPreloadStates((current) => ({ ...current, [item.id]: 'loading' }));
        try {
          for (const resource of item.resources) {
            if (cancelled) return;
            await preloadResource(resource);
          }
          if (!cancelled) {
            setPreloadStates((current) => ({ ...current, [item.id]: 'ready' }));
          }
        } catch (error) {
          if (!cancelled) {
            setPreloadStates((current) => ({ ...current, [item.id]: 'error' }));
          }
        }
      }
    };

    runPreloadQueue();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const { type, payload } = event.data || {};

      if (type === 'PING_STORY_SCENE_COMPLETE') {
        logStoryEvent('story_scene_complete', {
          stage,
          sceneId: payload?.sceneId || stage,
          completed: true,
          status: 'completed'
        });
        setActiveModule(null);
        setStage('transition');
        return;
      }

      if (type === 'PING_STORY_NAVIGATE') {
        handleStoryNavigate(payload);
        return;
      }

      if (type === 'PING_STORY_MODULE_COMPLETE') {
        logStoryEvent('story_module_complete', {
          stage,
          sceneId: payload?.sceneId || activeModule?.sceneId || stage,
          targetModuleId: payload?.gameId || activeModule?.gameId || activeModule?.sceneId || stage,
          completed: true,
          status: payload?.status || 'completed'
        });
        return;
      }

      if (type === 'PING_STORY_COMPLETE') {
        logStoryEvent('story_session_complete', {
          stage,
          sceneId: payload?.sceneId || activeModule?.sceneId || stage,
          targetModuleId: payload?.gameId || activeModule?.gameId || activeModule?.sceneId || stage,
          completed: true,
          status: payload?.status || 'completed'
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [activeModule, handleStoryNavigate, logStoryEvent, stage]);

  const frame = useMemo(() => {
    if (stage === 'transition') {
      return (
        <video
          className="sparc-story-video"
          title="SPARC transition video"
          src={TRANSITION_VIDEO_SRC}
          controls
          autoPlay
          playsInline
          onLoadedData={() => {
            markPreloadReady('transition');
            logStoryEvent('story_module_loaded', {
              stage: 'transition',
              sceneId: 'transition',
              targetModuleId: 'transition',
              status: 'loaded'
            });
          }}
          onEnded={openNavigation}
        />
      );
    }

    if (stage === 'navigation') {
      return (
        <iframe
          className="sparc-story-iframe"
          title="SPARC navigation hub"
          src={NAVIGATION_SRC}
          allow="fullscreen; autoplay"
          allowFullScreen
          onLoad={() => {
            markPreloadReady('navigation');
            logStoryEvent('story_module_loaded', {
              stage: 'navigation',
              sceneId: 'navigation',
              targetModuleId: 'navigation',
              status: 'loaded'
            });
          }}
        />
      );
    }

    if (stage === 'html-module' && activeModule) {
      return (
        <iframe
          className="sparc-story-iframe"
          title={activeModule.title}
          src={activeModule.src}
          allow="fullscreen; autoplay; gamepad"
          allowFullScreen
          onLoad={() => {
            markPreloadReady(mapModuleToPreloadId(activeModule));
            logStoryEvent('story_module_loaded', {
              stage: 'html-module',
              sceneId: activeModule.sceneId,
              targetModuleId: getModuleTelemetryId(activeModule),
              status: 'loaded'
            });
          }}
        />
      );
    }

    if (stage === 'unity-module' && activeModule) {
      return (
        <GameEmbed
          embedded
          gameIdOverride={activeModule.gameId}
          storyContext={{
            storyId: 'sparc',
            sceneId: activeModule.sceneId || stage,
            sequenceId: activeModule.kind === 'newton-sequence' ? 'newton-physics-challenge' : null,
            sequenceIndex: activeModule.sequenceIndex ?? null
          }}
          onUnityLoaded={() => {
            markPreloadReady(mapModuleToPreloadId(activeModule));
            logStoryEvent('story_module_loaded', {
              stage: 'unity-module',
              sceneId: activeModule.sceneId || stage,
              targetModuleId: activeModule.gameId,
              sequenceId: activeModule.kind === 'newton-sequence' ? 'newton-physics-challenge' : undefined,
              sequenceIndex: activeModule.sequenceIndex ?? undefined,
              status: 'loaded'
            });
          }}
          onUnityGameEvent={advanceNewtonSequence}
        />
      );
    }

    return (
      <iframe
        className="sparc-story-iframe"
        title="SPARC intro scene"
        src={INTRO_SRC}
        allow="fullscreen; autoplay"
        allowFullScreen
      />
    );
  }, [activeModule, advanceNewtonSequence, logStoryEvent, markPreloadReady, openNavigation, stage]);

  const currentTitle = activeModule?.kind === 'newton-sequence'
    ? `Newton Physics Challenge - ${activeModule.title}`
    : activeModule?.title || stageLabels[stage] || 'SPARC Story';
  const readyCount = PRELOAD_ITEMS.filter((item) => preloadStates[item.id] === 'ready').length;
  const loadingCount = PRELOAD_ITEMS.filter((item) => preloadStates[item.id] === 'loading').length;
  const errorCount = PRELOAD_ITEMS.filter((item) => preloadStates[item.id] === 'error').length;
  const allReady = readyCount === PRELOAD_ITEMS.length;
  const cacheState = allReady ? 'ready' : loadingCount > 0 ? 'loading' : errorCount > 0 ? 'error' : 'queued';
  const prepareChipText = errorCount
    ? `${readyCount}/${PRELOAD_ITEMS.length} ready, ${errorCount} on demand`
    : `Preparing ${readyCount}/${PRELOAD_ITEMS.length}`;
  const activePreloadId = stage === 'transition' || stage === 'navigation'
    ? stage
    : activeModule
      ? mapModuleToPreloadId(activeModule)
      : 'intro';

  return (
    <main className="sparc-story-page">
      <section className="sparc-story-shell" aria-label="SPARC story player">
        <header className="sparc-story-header">
          <div>
            <Link to="/" className="sparc-story-back">
              <ArrowLeft size={18} />
              <span>Back to Ping</span>
            </Link>
            <h1>SPARC Story</h1>
            <p>{currentTitle}</p>
          </div>
          <div className="sparc-story-header-actions">
            {!allReady && (
              <div
                className={`sparc-story-prepare-chip is-${cacheState}`}
                data-testid="sparc-prepare-chip"
                aria-live="polite"
              >
                {cacheState === 'error' ? <AlertTriangle size={14} /> : <Loader2 size={14} />}
                <span>{prepareChipText}</span>
              </div>
            )}
            <button className="sparc-story-tool-button" type="button" onClick={openNavigation}>
              <Map size={17} />
              Return to Hub
            </button>
            <button className="sparc-story-tool-button" type="button" onClick={restartStory}>
              <RotateCcw size={17} />
              Restart Story
            </button>
            <button className="sparc-story-tool-button" type="button" onClick={handleFullscreen}>
              <Maximize2 size={17} />
              Fullscreen
            </button>
          </div>
        </header>

        <div className="sparc-story-layout">
          <div className="sparc-story-theater" ref={theaterRef}>
            <div className="sparc-story-frame" aria-label="Story game frame">
              {frame}
            </div>
            <div className="sparc-story-helpbar" aria-label="Current scene controls">
              <Keyboard size={18} />
              <div>
                <strong>{currentTitle}</strong>
                <span>{helpText[stage]}</span>
              </div>
            </div>
          </div>

          <aside className="sparc-story-portal" aria-label="Story map">
            <div className="sparc-story-portal-top">
              <Map size={17} aria-hidden="true" />
              <div>
                <h2>Story Map</h2>
                <p>Scenes stay ready in the background.</p>
              </div>
            </div>

            <div className="sparc-story-preload-list">
              <div
                className={`sparc-story-preload-item is-${activePreloadId === 'intro' ? 'current' : 'ready'}`}
              >
                <span className="sparc-story-preload-icon" aria-hidden="true">
                  {activePreloadId === 'intro' ? <Map size={16} /> : <CheckCircle2 size={16} />}
                </span>
                <span className="sparc-story-preload-name">Intro Scene</span>
                <span className="sparc-story-preload-status">
                  {activePreloadId === 'intro' ? 'Current' : 'Done'}
                </span>
              </div>
              {PRELOAD_ITEMS.map((item) => {
                const status = preloadStates[item.id];
                const isCurrent = activePreloadId === item.id;
                return (
                  <div
                    className={`sparc-story-preload-item is-${status}${isCurrent ? ' is-current' : ''}`}
                    data-testid={`preload-${item.id}`}
                    key={item.id}
                  >
                    <span className="sparc-story-preload-icon" aria-hidden="true">
                      {isCurrent && <Map size={16} />}
                      {!isCurrent && status === 'ready' && <CheckCircle2 size={16} />}
                      {!isCurrent && status === 'loading' && <Loader2 size={16} />}
                      {!isCurrent && status === 'queued' && <Circle size={16} />}
                      {!isCurrent && status === 'error' && <AlertTriangle size={16} />}
                    </span>
                    <span className="sparc-story-preload-name">{item.label}</span>
                    <span className="sparc-story-preload-status">
                      {isCurrent ? 'Current' : preloadStatusText[status]}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="sparc-story-current">
              {stage === 'transition' && <Film size={18} />}
              {(stage === 'html-module' || stage === 'unity-module') && <Gamepad2 size={18} />}
              {stage === 'navigation' && <Map size={18} />}
              {stage === 'intro' && <Loader2 size={18} />}
              <span>{currentTitle}</span>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default SparcStoryPlayer;
